import { useState, useEffect } from "react";
import { saveAs } from "file-saver";
import { api, assetUrl, formatDate } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import { DEFAULT_BRANDING } from "@/lib/branding";

function sigForPrint(sig: string) {
  return sig.startsWith("/api/") ? assetUrl(sig) : sig;
}

function renderSignature(sig: string, role: "instructor" | "evaluator") {
  if (!sig) return `<div class="sig-text sig-${role}">&nbsp;</div>`;
  if (sig.startsWith("/api/") || sig.startsWith("http") || sig.startsWith("data:")) {
    const src = sig.startsWith("/api/") ? assetUrl(sig) : sig;
    return `<img src="${src}" alt="Firma" style="height:44px;max-width:160px;object-fit:contain" />`;
  }
  return `<div class="sig-text sig-${role}">${sig}</div>`;
}

function stampSizePx(raw?: string) {
  const n = Number(raw || 96);
  if (Number.isNaN(n)) return 96;
  return Math.min(160, Math.max(48, n));
}

export function buildCertificateHtml(cert: {
  certificate_number: string;
  student_name: string;
  course_name: string;
  course_category?: string;
  course_duration?: string;
  hours_completed: number;
  issue_date: string;
  instructor_signature: string;
  evaluator_signature: string;
  qr_code?: string;
}, logoUrl = DEFAULT_BRANDING.logo_full, stampUrl?: string, iconUrl = DEFAULT_BRANDING.logo_icon, stampSize = 96) {
  const qrImg = cert.qr_code
    ? `<img src="${cert.qr_code}" alt="QR" style="width:52px;height:52px" />`
    : `<span style="font-size:8px;color:#aaa">QR</span>`;

  const size = stampSizePx(String(stampSize));
  const hasStamp = Boolean(stampUrl);
  const sealBlock = hasStamp
    ? `<div class="seal"><img src="${stampUrl}" alt="Sello" /></div>`
    : "";

  const safeLogo = logoUrl || DEFAULT_BRANDING.logo_full;
  const safeIcon = iconUrl || DEFAULT_BRANDING.logo_icon;

  return `<!DOCTYPE html>
<html><head>
<title>Certificado ${cert.certificate_number}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Cinzel:wght@400;700&family=Great+Vibes&family=Allura&family=Dancing+Script:wght@600&family=Satisfy&display=swap');
  * { margin:0; padding:0; box-sizing:border-box; }
  html, body { background:#fff !important; }
  body { font-family:'Inter',sans-serif; display:flex; justify-content:center; align-items:center; min-height:100vh; padding:20px; }
  @media print {
    body, html, .cert { background-color: #fff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  }
  .cert { width:900px; min-height:620px; background:#fff !important; border:none; position:relative; padding:36px 64px 28px; overflow:hidden; display:flex; flex-direction:column; }
  .watermark { position:absolute; bottom:0; left:50%; transform:translate(-50%, 50%); opacity:0.04; z-index:0; pointer-events:none; }
  .watermark img { width:800px; height:auto; max-width:none; }
  .seal { position:absolute; top:28px; right:40px; width:${size}px; height:${size}px; border:none; background:transparent; display:flex; align-items:center; justify-content:center; z-index:2; }
  .seal img { width:100%; height:100%; object-fit:contain; }
  .cert-inner { position:relative; z-index:1; text-align:center; background:transparent; flex:1; display:flex; flex-direction:column; min-height:0; }
  .logo-area { min-height:86px; display:flex; align-items:center; justify-content:center; margin-bottom:4px; }
  .logo-area img { height:96px; max-width:420px; object-fit:contain; display:block; }
  .org-name { font-size:12px; letter-spacing:3px; text-transform:uppercase; color:#38424E; font-weight:600; margin-top:4px; }
  .divider { width:100px; height:2px; background:#D4A03C; margin:10px auto 14px; }
  .title { font-family:'Cinzel',serif; font-size:17px; color:#38424E; letter-spacing:2px; margin-bottom:14px; }
  .name { font-family:'Cinzel',serif; font-size:34px; color:#38424E; font-weight:700; margin:8px 0 18px; line-height:1.15; }
  .course-name { font-size:18px; color:#444; font-weight:700; margin-bottom:4px; line-height:1.25; }
  .course-level { font-size:13px; color:#D4A03C; font-weight:600; letter-spacing:1px; margin-bottom:0; }
  .spacer-md { height:28px; }
  .spacer-grow { flex:1; min-height:24px; }
  .details { display:flex; justify-content:space-between; width:80%; margin:0 auto; }
  .detail-value { font-size:18px; font-weight:700; color:#38424E; }
  .detail-label { font-size:10px; color:#888; text-transform:uppercase; letter-spacing:2px; margin-top:6px; }
  .signatures { display:grid !important; grid-template-columns:1fr 1fr; gap:24px; width:82%; margin:0 auto; align-items:end; }
  .signatures > div { text-align:center; }
  .sig-line { width:150px; height:1px; background:#38424E; margin:4px auto 6px; }
  .sig-role { font-size:9px; color:#888; text-transform:uppercase; letter-spacing:1px; }
  .sig-text { color:#1f2937; line-height:1.05; padding:0 6px 2px; min-height:32px; display:block; white-space:nowrap; transform:none !important; }
  .sig-instructor { font-family:'Great Vibes', 'Dancing Script', cursive; font-size:24px; }
  .sig-evaluator { font-family:'Allura', 'Satisfy', cursive; font-size:26px; }
  .footer-row { display:flex; justify-content:space-between; align-items:flex-end; margin-top:14px; padding-top:4px; }
  .cert-num { font-size:9px; color:#bbb; font-family:monospace; }
</style></head><body>
<div class="cert">
  <div class="watermark"><img src="${safeIcon}" alt="Watermark" /></div>
  ${sealBlock}
  <div class="cert-inner">
    <div class="logo-area"><img src="${safeLogo}" alt="KSM Services" /></div>
    <div class="org-name">Training Management</div>
    <div class="divider"></div>
    <div class="title">Otorga el Presente Certificado a</div>
    <div class="name">${cert.student_name}</div>
    <div class="course-name">${cert.course_name}</div>
    <div class="course-level">${cert.course_category || "Capacitación"} — ${cert.course_duration || ""}</div>
    <div class="spacer-md"></div>
    <div class="details">
      <div><div class="detail-value">${cert.hours_completed}h</div><div class="detail-label">Horas</div></div>
      <div><div class="detail-value">${formatDate(cert.issue_date)}</div><div class="detail-label">Emisión</div></div>
      <div><div class="detail-value">${cert.certificate_number}</div><div class="detail-label" style="text-transform:none">No. Certificado</div></div>
    </div>
    <div class="spacer-grow"></div>
    <div class="signatures">
      <div>${renderSignature(cert.instructor_signature, "instructor")}<div class="sig-line"></div><div class="sig-role">Instructor</div></div>
      <div>${renderSignature(cert.evaluator_signature, "evaluator")}<div class="sig-line"></div><div class="sig-role">Evaluador</div></div>
    </div>
    <div class="footer-row">
      <div>${qrImg}</div>
      <div class="cert-num">${cert.certificate_number}</div>
    </div>
  </div>
</div></body></html>`;
}

interface CertificatePreviewProps {
  cert: {
    id: string;
    certificate_number: string;
    student_id: string;
    course_id?: string | null;
    hours_completed: number;
    issue_date: string;
    instructor_signature: string;
    evaluator_signature: string;
    status: string;
    qr_code?: string;
  };
  studentName?: string;
  courseName?: string;
  courseCategory?: string;
  courseDuration?: string;
  onClose: () => void;
}

function SigDisplay({ sig, role }: { sig: string; role: "Instructor" | "Evaluador" }) {
  const isImg = sig.startsWith("/api/") || sig.startsWith("http") || sig.startsWith("data:");
  const fontClass =
    role === "Instructor"
      ? "font-['Great_Vibes','Dancing_Script',cursive] text-[24px]"
      : "font-['Allura','Satisfy',cursive] text-[26px]";

  return (
    <div className="text-center w-full">
      <div className="w-full max-w-[180px] border-b border-[#38424E] mb-1 mx-auto min-h-[40px] flex items-end justify-center">
        {isImg ? (
          <img src={assetUrl(sig)} alt={`Firma ${role}`} className="h-9 max-w-[140px] object-contain" />
        ) : (
          <span className={`text-[#1f2937] leading-none pb-1 ${fontClass}`}>{sig}</span>
        )}
      </div>
      <div className="text-[9px] text-gray-500 uppercase tracking-wider">{role}</div>
    </div>
  );
}

export default function CertificatePreview({ cert, studentName, courseName, courseCategory, courseDuration, onClose }: CertificatePreviewProps) {
  const branding = useBranding();
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const preferredLogo =
    branding.certificate_logo ||
    branding.logo_dark ||
    DEFAULT_BRANDING.logo_full ||
    branding.logo_icon;
  const [logoSrc, setLogoSrc] = useState(preferredLogo);
  const stampPx = stampSizePx(branding.certificate_stamp_size);

  useEffect(() => {
    setLogoSrc(preferredLogo);
  }, [preferredLogo]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const blob = await api.downloadCertificatePdf(cert.id);
      saveAs(blob, `Certificado_${cert.certificate_number}.pdf`);
    } catch (err) {
      alert("Error al descargar el PDF. Intente nuevamente.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(buildCertificateHtml({
      certificate_number: cert.certificate_number,
      student_name: studentName || "Estudiante",
      course_name: courseName || "Curso",
      course_category: courseCategory,
      course_duration: courseDuration,
      hours_completed: cert.hours_completed,
      issue_date: cert.issue_date,
      instructor_signature: sigForPrint(cert.instructor_signature),
      evaluator_signature: sigForPrint(cert.evaluator_signature),
      qr_code: cert.qr_code,
    }, logoSrc || DEFAULT_BRANDING.logo_full, branding.certificate_stamp || undefined, branding.logo_icon || DEFAULT_BRANDING.logo_icon, stampPx));
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 800);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Allura&family=Dancing+Script:wght@600&family=Satisfy&family=Cinzel:wght@400;700&display=swap" />
      <div className="bg-white rounded-xl overflow-hidden max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative bg-white p-8 md:p-10 m-4 min-h-[560px] flex flex-col overflow-hidden">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 pointer-events-none z-0 opacity-[0.04]">
            <img src={branding.logo_icon || DEFAULT_BRANDING.logo_icon} alt="Watermark" className="w-[800px] max-w-none object-contain" />
          </div>
          {branding.certificate_stamp ? (
            <div
              className="absolute top-6 right-6 flex items-center justify-center"
              style={{ width: stampPx, height: stampPx }}
            >
              <img src={branding.certificate_stamp} alt="Sello" className="w-full h-full object-contain" />
            </div>
          ) : null}
          <div className="text-center relative z-10 flex flex-col flex-1 min-h-0">
            <div className="min-h-[86px] flex items-center justify-center mb-1">
              <img
                src={logoSrc}
                alt="KSM Services"
                className="h-[96px] max-w-[420px] object-contain mx-auto"
                onError={() => {
                  if (logoSrc !== DEFAULT_BRANDING.logo_full) {
                    setLogoSrc(DEFAULT_BRANDING.logo_full);
                  }
                }}
              />
            </div>
            <div className="text-xs tracking-[3px] uppercase text-[#38424E] font-semibold">Training Management</div>
            <div className="w-24 h-0.5 bg-[#D4A03C] mx-auto my-3.5"></div>
            <div className="text-sm tracking-[2px] uppercase text-[#38424E] font-light mb-3.5">Otorga el Presente Certificado a</div>
            <div className="text-[2rem] md:text-[2.15rem] font-bold text-[#38424E] mb-4 font-serif leading-tight">{studentName || "Estudiante"}</div>
            <div className="text-lg md:text-xl font-bold text-gray-700 leading-snug">{courseName || "Curso"}</div>
            <div className="text-sm text-[#D4A03C] font-semibold tracking-wider">{courseCategory || "Capacitación"} — {courseDuration || ""}</div>
            <div className="h-7" />
            <div className="flex justify-between w-4/5 mx-auto">
              <div>
                <div className="text-xl font-bold text-[#38424E]">{cert.hours_completed}h</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1.5">Horas</div>
              </div>
              <div>
                <div className="text-xl font-bold text-[#38424E]">{formatDate(cert.issue_date)}</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1.5">Emisión</div>
              </div>
              <div>
                <div className="text-lg font-bold text-[#38424E] font-mono">{cert.certificate_number}</div>
                <div className="text-[10px] text-gray-500 tracking-wider mt-1.5">No. Certificado</div>
              </div>
            </div>
            <div className="flex-1 min-h-6" />
            <div className="grid grid-cols-2 gap-6 w-4/5 mx-auto">
              <SigDisplay sig={cert.instructor_signature} role="Instructor" />
              <SigDisplay sig={cert.evaluator_signature} role="Evaluador" />
            </div>
            <div className="flex justify-between items-end mt-3.5 pt-1">
              {cert.qr_code ? <img src={cert.qr_code} alt="QR" className="w-12 h-12" /> : <div className="w-12 h-12 bg-gray-100 flex items-center justify-center text-[8px] text-gray-400">QR</div>}
              <span className="text-[9px] text-gray-400 font-mono">{cert.certificate_number}</span>
            </div>
          </div>
        </div>
        <div className="bg-[var(--color-bg-secondary)] p-4 flex items-center justify-between border-t border-[var(--color-border)]">
          <span className="text-sm text-[var(--color-text-muted)]">Fondo blanco · Firmas digitales</span>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] px-4 py-2 cursor-pointer">Cerrar</button>
            <button onClick={handlePrint} className="text-sm font-medium bg-[var(--color-bg-card)] text-[var(--color-text-primary)] border border-[var(--color-border)] px-5 py-2.5 rounded-lg hover:border-[var(--color-border-light)] flex items-center gap-2 cursor-pointer whitespace-nowrap">
              <i className="ri-printer-line"></i> Imprimir
            </button>
            <button onClick={handleDownloadPdf} disabled={downloadingPdf} className="text-sm font-medium bg-[var(--color-accent)] text-white px-5 py-2.5 rounded-lg hover:bg-[var(--color-accent-hover)] flex items-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-50">
              {downloadingPdf ? (
                <><i className="ri-loader-4-line animate-spin"></i> Generando PDF...</>
              ) : (
                <><i className="ri-download-2-line"></i> Descargar PDF</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
